import type { Node, Viewport } from 'reactflow';
import { BLOCK_NODE_WIDTH } from '../../constants';

// Below this zoom the marks are unreadable, so a jump raises zoom to 1; above
// it the user's zoom is kept.
const MIN_READABLE_ZOOM = 0.75;

// Duration scales with screen distance (clamped), capping the felt peak speed;
// a fixed duration made long jumps streak.
const MIN_DURATION_MS = 200;
const MAX_DURATION_MS = 620;
const DISTANCE_PX_PER_MS = 2.5;

// Past this many pane-widths, hard-cut to just short of the target (along the
// travel direction, so orientation survives) and glide the final stretch; the
// full flight conveys nothing but "it's far".
const TELEPORT_PANE_WIDTHS = 1.5;
const GLIDE_PANE_WIDTHS = 0.75;

// Smoothstep: zero velocity at both ends, peak 1.5x average. Ease-out lurched
// (its 3x peak lands on frame one).
const smoothstep = (t: number): number => t * t * (3 - 2 * t);

// Pan the viewport in a straight line to centre `node`; returns a cancel
// function. Hand-rolled because React Flow v11's animated setCenter flies d3's
// zoom-out-and-back flight path, which read as churn. `snap` lands instantly.
export function panViewportToNode({
  node,
  viewport,
  container,
  setViewport,
  snap = false,
}: {
  node: Pick<Node, 'position' | 'positionAbsolute' | 'width' | 'height'>;
  viewport: Viewport;
  container: { width: number; height: number };
  setViewport: (viewport: Viewport) => void;
  snap?: boolean;
}): () => void {
  const zoom = viewport.zoom < MIN_READABLE_ZOOM ? 1 : viewport.zoom;
  // positionAbsolute, so containerised nodes centre where they actually sit.
  const position = node.positionAbsolute ?? node.position;
  const centreX = position.x + (node.width ?? BLOCK_NODE_WIDTH) / 2;
  const centreY = position.y + (node.height ?? 0) / 2;
  const target: Viewport = {
    x: container.width / 2 - centreX * zoom,
    y: container.height / 2 - centreY * zoom,
    zoom,
  };

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (snap || prefersReducedMotion) {
    setViewport(target);
    return () => {};
  }

  // Current viewport centre in flow coordinates; distance in screen px at the
  // landing zoom.
  const currentCentreX = (container.width / 2 - viewport.x) / viewport.zoom;
  const currentCentreY = (container.height / 2 - viewport.y) / viewport.zoom;
  let distance = Math.hypot(centreX - currentCentreX, centreY - currentCentreY) * zoom;

  let from = viewport;
  if (distance > TELEPORT_PANE_WIDTHS * container.width) {
    const keep = (GLIDE_PANE_WIDTHS * container.width) / distance;
    const cutX = centreX + (currentCentreX - centreX) * keep;
    const cutY = centreY + (currentCentreY - centreY) * keep;
    from = {
      x: container.width / 2 - cutX * zoom,
      y: container.height / 2 - cutY * zoom,
      zoom,
    };
    setViewport(from);
    distance = GLIDE_PANE_WIDTHS * container.width;
  }

  const duration = Math.min(
    MAX_DURATION_MS,
    Math.max(MIN_DURATION_MS, distance / DISTANCE_PX_PER_MS),
  );
  const zoomRatio = target.zoom / from.zoom;

  let frame: number | null = null;
  let startTime: number | null = null;

  const step = (now: number) => {
    if (startTime === null) {
      startTime = now;
    }
    const progress = Math.min((now - startTime) / duration, 1);
    const eased = smoothstep(progress);
    setViewport({
      x: from.x + (target.x - from.x) * eased,
      y: from.y + (target.y - from.y) * eased,
      // Log-space interpolation: perceptually uniform for scale.
      zoom: from.zoom * zoomRatio ** eased,
    });
    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      frame = null;
    }
  };

  frame = requestAnimationFrame(step);

  return () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }
  };
}
